import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { rethrowHandledHttpError } from '@core/http-interceptors/rethrow-handled-http-error';
import { environment } from '@environments/environment';

const APIURL = environment.apiUrl;

/** Keys of the separately sold modules. Must match `Modules` in the API. */
export type ModuleKey = 'doors';

/** A module and the caller's own anlæg that have bought it (`location_module`). */
export interface ModuleLocations {
  key: ModuleKey;
  locationIds: number[];
}

/** One anlæg on the SuperAdmin module page. */
export interface ModuleLocation {
  locationId: number;
  locationName: string | null;
  active: boolean;
  enabled: boolean;
}

/** What Indstillinger → Moduler says about each module; a module here is a column there. */
export const MODULE_LABELS: Record<
  ModuleKey,
  { title: string; description: string; whenOff: string }
> = {
  doors: {
    title: 'Døre',
    description:
      'Alt om døre på anlægget: dørkoder og brikker (bookingernes og brugernes), låseloggen, scan brik, ' +
      'låsesystemer, døre der står åbne under bookinger eller åbnes med knap, link eller fra administrationen. ' +
      'Sælges pr. anlæg.',
    whenOff:
      'Anlæggets lokaler holder op med at give bookinger dørkoder, og dørene holder op med at stå åbne og kan ikke ' +
      'åbnes med knap, link eller fra administrationen. Der kan ikke laves nye koder, brikker eller låsesystemer, og ' +
      'loggen og dørene skjules. Eksisterende koder og opsætning beholdes.',
  },
};

/** The anlæg in `modules` that have bought `key`. */
export function moduleLocationIds(modules: ModuleLocations[], key: ModuleKey): number[] {
  return modules.find((m) => m.key === key)?.locationIds ?? [];
}

/**
 * The separately sold modules, bought per anlæg (`location_module`). `GET /modules` answers with the
 * caller's own anlæg only (licensed ∩ visible), so "do I have it" is "is the list non-empty".
 *
 * Read once per session and shared; a failed read counts as "none" for that call and is asked again
 * next time, so a tenant database without the table only loses the module menus. A change made on
 * the module page reloads it, so the pages follow without a page reload. Forgotten when the
 * signed-in user changes (`UserService.clearPermissionCache`).
 */
@Injectable({ providedIn: 'root' })
export class ModuleService {
  private readonly http = inject(HttpClient);

  private modules$?: Observable<ModuleLocations[]>;
  private readonly modules = signal<ModuleLocations[]>([]);

  list(): Observable<ModuleLocations[]> {
    if (this.modules$) {
      return this.modules$;
    }

    const modules$: Observable<ModuleLocations[]> = this.http
      .get<ModuleLocations[]>(`${APIURL}/modules`)
      .pipe(
        // AuthInterceptor hands a network error, a 401 or a 403 on as a value; cached, it would be
        // the module list for the rest of the session.
        rethrowHandledHttpError(),
        tap((modules) => this.modules.set(modules)),
        catchError(() => {
          // Not cached: a transient failure (token refresh, a 5xx) must not hide the module for
          // the rest of the session. Only if it is still this read that is cached: a `clear()`
          // meanwhile may have started a fresh one. The signal says the same as the stream, so the
          // menus and the guard agree.
          if (this.modules$ === modules$) {
            this.modules$ = undefined;
            this.modules.set([]);
          }
          return of([] as ModuleLocations[]);
        }),
        shareReplay(1),
      );
    this.modules$ = modules$;
    return modules$;
  }

  /** Forgets the list — call when the signed-in user changes; the next question reads it again. */
  clear(): void {
    this.modules$ = undefined;
    this.modules.set([]);
  }

  /** The caller's anlæg that have bought the module, kept current. */
  locationIds(key: ModuleKey): Signal<number[]> {
    this.list().subscribe();
    return computed(() => moduleLocationIds(this.modules(), key));
  }

  /** True when one of the signed-in user's own anlæg has the module, kept current — what menus ask. */
  has(key: ModuleKey): Signal<boolean> {
    const ids = this.locationIds(key);
    return computed(() => ids().length > 0);
  }

  /** The same as {@link has}, once the list is read — what guards ask. */
  hasForMyLocations(key: ModuleKey): Observable<boolean> {
    return this.list().pipe(map((modules) => moduleLocationIds(modules, key).length > 0));
  }

  /** SuperAdmin only — the API answers 403 to anyone else. */
  listLocations(key: ModuleKey): Observable<ModuleLocation[]> {
    return this.http.get<ModuleLocation[]>(`${APIURL}/modules/${key}/locations`);
  }

  /** SuperAdmin only. Reloads the caller's own list, which the pages read. */
  setLocation(key: ModuleKey, locationId: number, enabled: boolean): Observable<ModuleLocation> {
    return this.http
      .put<ModuleLocation>(`${APIURL}/modules/${key}/locations/${locationId}`, { enabled })
      .pipe(
        tap(() => {
          this.modules$ = undefined;
          this.list().subscribe();
        }),
      );
  }
}

/** Sends the user to the unauthorized page unless one of their anlæg has the module. */
export function moduleGuard(key: ModuleKey): CanActivateFn {
  return () => {
    const router = inject(Router);
    return inject(ModuleService)
      .hasForMyLocations(key)
      .pipe(map((has) => has || router.parseUrl('/authentication/unauthorized')));
  };
}
